import { Linkedin, Quote } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

export default function Team() {
  return (
    <section id="team" data-testid="team-section" className="py-28 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        <Reveal className="max-w-2xl mb-16">
          <Eyebrow>Our Team</Eyebrow>
          <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl text-[#1b2a4e] tracking-tight leading-[1.1]">
            Senior counsel. <span className="italic text-[#a8824f]">Rigorous analysis.</span>
          </h2>
          <p className="text-[#45506b] mt-5 text-sm sm:text-base leading-relaxed">
            ComplyWise Consult combines bar-level legal judgement with hands-on analytical support —
            so every engagement gets both strategic direction and meticulous delivery.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <Reveal>
            <article
              data-testid="team-member-founder"
              className="h-full rounded-3xl bg-white border border-[#e6e1d5] p-8 sm:p-10 hover:border-[#a8824f]/60 hover:shadow-[0_20px_50px_rgba(27,42,78,0.08)] transition-all duration-300 flex flex-col"
            >
              <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                  <span className="font-mono-alt text-[10px] uppercase tracking-[0.3em] text-[#a8824f] block mb-2">
                    Founder &amp; Managing Director
                  </span>
                  <h3 className="font-display text-2xl sm:text-[1.7rem] text-[#1b2a4e]">Janice Acquaah</h3>
                </div>
                <a
                  data-testid="founder-linkedin-link"
                  href="https://www.linkedin.com/in/janice-acquaah-5a3b0a8/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2.5 rounded-full border border-[#e6e1d5] text-[#45506b] hover:text-[#a8824f] hover:border-[#a8824f]/60 transition-colors shrink-0 focus:outline-none focus:ring-2 focus:ring-[#a8824f]"
                  aria-label="Janice Acquaah on LinkedIn"
                >
                  <Linkedin size={17} />
                </a>
              </div>
              <div className="space-y-4 text-[#45506b] text-sm sm:text-[15px] leading-relaxed flex-1">
                <p>
                  Janice Acquaah is a dedicated legal and financial services regulatory compliance
                  professional, dual-qualified at the Ghana Bar (1995) and the England &amp; Wales Bar
                  (2008), with over ten years' experience in the City of London's financial services
                  sector — spanning compliance frameworks, financial crime prevention, risk management
                  and governance.
                </p>
                <p>
                  Her work covers regulatory change, policy development, control design, monitoring and
                  advisory support across a wide range of financial institutions: engaging senior
                  stakeholders, managing regulatory interactions and driving improvements in compliance
                  maturity and operational resilience.
                </p>
              </div>
              <div className="mt-6 flex items-start gap-3 border-l-2 border-[#a8824f] pl-5">
                <Quote size={17} className="text-[#a8824f] shrink-0 mt-0.5" />
                <p className="font-display italic text-base text-[#1b2a4e] leading-relaxed">
                  Compliance should be embedded into culture, operations and customer outcomes —
                  building a safer, more transparent financial system.
                </p>
              </div>
              <div className="mt-6 flex flex-wrap gap-2.5">
                {["Ghana Bar · 1995", "England & Wales Bar · 2008", "Financial Crime Prevention", "Governance & Risk"].map((chip) => (
                  <span
                    key={chip}
                    className="font-mono-alt text-[10px] uppercase tracking-[0.2em] text-[#45506b] px-3.5 py-1.5 rounded-full border border-[#e6e1d5] bg-[#faf9f6]"
                  >
                    {chip}
                  </span>
                ))}
              </div>
            </article>
          </Reveal>

          <Reveal delay={0.12}>
            <article
              data-testid="team-member-natalie"
              className="h-full rounded-3xl bg-white border border-[#e6e1d5] p-8 sm:p-10 hover:border-[#a8824f]/60 hover:shadow-[0_20px_50px_rgba(27,42,78,0.08)] transition-all duration-300 flex flex-col"
            >
              <img
                data-testid="natalie-portrait"
                src="/natalie-annan.jpeg"
                alt="Natalie Annan, Principal Consultant for Business and Product Development at ComplyWise Consult"
                className="w-full aspect-[4/4.4] rounded-2xl object-cover object-top border border-[#e6e1d5] mb-6"
              />
              <span className="font-mono-alt text-[10px] uppercase tracking-[0.3em] text-[#a8824f] block mb-2">
                Principal Consultant — Business &amp; Product Development
              </span>
              <h3 className="font-display text-2xl sm:text-[1.7rem] text-[#1b2a4e] mb-6">Natalie Annan</h3>
              <div className="space-y-4 text-[#45506b] text-sm sm:text-[15px] leading-relaxed flex-1">
                <p>
                  With a professional background in Human Resource Management and General Management,
                  and experience in business development, strategy and Go-To-Market execution,
                  Natalie brings a practical, solution-driven approach to business growth and product
                  development — strengthened by a journey across management and the fintech industry.
                </p>
                <p>
                  At ComplyWise Consult, she leads business and product development: identifying growth
                  opportunities, developing strategic partnerships, shaping new products and driving the
                  firm's digital initiatives — including its e-learning platform for compliance training
                  and professional development.
                </p>
              </div>
              <div className="mt-6 flex flex-wrap gap-2.5">
                {["Business Development", "Strategy & GTM", "Strategic Partnerships", "E-Learning Platform"].map((chip) => (
                  <span
                    key={chip}
                    className="font-mono-alt text-[10px] uppercase tracking-[0.2em] text-[#45506b] px-3.5 py-1.5 rounded-full border border-[#e6e1d5] bg-[#faf9f6]"
                  >
                    {chip}
                  </span>
                ))}
              </div>
            </article>
          </Reveal>

          <Reveal delay={0.24}>
            <article
              data-testid="team-member-angel-lyric"
              className="h-full rounded-3xl bg-white border border-[#e6e1d5] p-8 sm:p-10 hover:border-[#a8824f]/60 hover:shadow-[0_20px_50px_rgba(27,42,78,0.08)] transition-all duration-300 flex flex-col"
            >
              <img
                data-testid="angel-lyric-portrait"
                src="/angel-lyric-koomson.png"
                alt="Angel-Lyric Appiah Koomson, Compliance Analyst at ComplyWise Consult"
                className="w-full aspect-[4/4.4] rounded-2xl object-cover object-top border border-[#e6e1d5] mb-6"
              />
              <span className="font-mono-alt text-[10px] uppercase tracking-[0.3em] text-[#a8824f] block mb-2">
                Compliance Analyst
              </span>
              <h3 className="font-display text-2xl sm:text-[1.7rem] text-[#1b2a4e] mb-6">Angel-Lyric Appiah Koomson</h3>
              <div className="space-y-4 text-[#45506b] text-sm sm:text-[15px] leading-relaxed flex-1">
                <p>
                  Angel-Lyric Appiah Koomson is a compliance professional with hands-on experience in
                  due diligence, customer verification, claims investigations and financial crime
                  prevention — supporting organisations to strengthen compliance processes and uphold
                  regulatory standards.
                </p>
                <p>
                  As a Consultant to the Official Liquidator through ABG Consult, she reviewed and
                  verified over 5,000 claims submissions, conducted KYC checks and identified potential
                  fraudulent activity. She holds ICA certifications in AML, KYC &amp; CDD, and Managing
                  Sanctions Risk.
                </p>
              </div>
              <div className="mt-6 flex flex-wrap gap-2.5">
                {["ICA Certified · AML", "KYC & CDD", "Sanctions Risk", "Claims Investigations"].map((chip) => (
                  <span
                    key={chip}
                    className="font-mono-alt text-[10px] uppercase tracking-[0.2em] text-[#45506b] px-3.5 py-1.5 rounded-full border border-[#e6e1d5] bg-[#faf9f6]"
                  >
                    {chip}
                  </span>
                ))}
              </div>
            </article>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
